import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs } from '@/components/site/Breadcrumbs';
import { FaqList } from '@/components/site/FaqList';
import { SiteMeta } from '@/components/site/SiteMeta';
import { appPriceLabel } from '@/content/apps';
import { trialHref } from '@/content/plans';
import { findSector, sectors } from '@/content/sectors';
import { NotFoundPage } from '@/pages/site/seo/NotFoundPage';
import { appPath, findBuildApp } from '@/seo/catalog';

export function SectorPage() {
  const { slug = '' } = useParams();
  const sector = findSector(slug);
  if (!sector) return <NotFoundPage />;

  // Só entra app que existe no catálogo do build: se sair do catálogo, some daqui.
  const apps = sector.apps.map(findBuildApp).filter((a) => a !== undefined);
  const flows = sector.flows.filter((flow) => !flow.app || findBuildApp(flow.app));
  const outros = sectors.filter((s) => s.slug !== sector.slug);

  return (
    <>
      <SiteMeta title={`${sector.searchTerm} | Operax`} description={sector.lead} path={`/setores/${sector.slug}`} />

      <section className="page-hero">
        <div className="hero-grid-bg" />
        <div className="shell page-hero-in rev">
          <Breadcrumbs
            items={[
              { name: 'Início', path: '/' },
              { name: 'Setores', path: '/setores' },
              { name: sector.name },
            ]}
          />
          <div className="eyebrow">{sector.name}</div>
          <h1>{sector.headline}</h1>
          <p className="lead">{sector.lead}</p>
          <div className="hero-cta" style={{ marginTop: 28 }}>
            <a className="btn btn-primary" href={trialHref()}>
              Assinar a Operax
            </a>
            <Link className="btn btn-ghost" to="/precos">
              Ver planos
            </Link>
          </div>
        </div>
      </section>

      <section className="pad">
        <div className="shell seo-article">
          <h2>O WhatsApp no dia a dia de {sector.name.toLowerCase()}</h2>
          {sector.context.map((paragraph) => (
            <p key={paragraph.slice(0, 32)}>{paragraph}</p>
          ))}

          <h2>O que muda com a Operax</h2>
          <ol className="seo-flows">
            {flows.map((flow) => {
              const app = flow.app ? findBuildApp(flow.app) : undefined;
              return (
                <li key={flow.title}>
                  <h3>{flow.title}</h3>
                  <p>{flow.text}</p>
                  {app ? (
                    <Link className="seo-flow-app" to={appPath(app.slug)}>
                      App {app.name}
                      {appPriceLabel(app) ? ` · ${appPriceLabel(app)}` : ''}
                    </Link>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      <section className="pad seo-soft">
        <div className="shell">
          <div className="sec-head rev">
            <div className="eyebrow">Apps do setor</div>
            <h2 className="h2">O que ativar para {sector.name.toLowerCase()}.</h2>
            <p className="lead" style={{ marginTop: 16 }}>
              Atendimento e funil vêm no plano. Os apps abaixo entram quando a operação pedir, no mesmo login.
            </p>
          </div>
          <div className="apps-grid" style={{ marginTop: 32 }}>
            {apps.map((app) => (
              <Link key={app.slug} className="app-card rev" to={appPath(app.slug)}>
                <div className="nm">{app.name}</div>
                <div className="ds">{app.description ?? ''}</div>
                <div className="app-price">{appPriceLabel(app) ?? 'Em breve'}</div>
                <span className="app-more">
                  Ver detalhes <span aria-hidden>→</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {sector.faq.length > 0 && (
        <section className="pad">
          <div className="shell">
            <div className="sec-head center rev">
              <h2 className="h2">Perguntas de quem é do setor</h2>
            </div>
            <FaqList entries={sector.faq} />
          </div>
        </section>
      )}

      <section className="pad seo-soft">
        <div className="shell">
          <div className="sec-head rev">
            <div className="eyebrow">Outros setores</div>
          </div>
          <ul className="seo-chips">
            {outros.map((s) => (
              <li key={s.slug}>
                <Link to={`/setores/${s.slug}`}>{s.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
